import { z } from 'zod';
import { IsoDateSchema, IsoDateTimeSchema, UuidSchema } from './common';
import { EventPropertyFilterSchema, QUERY_LIMITS } from './segments';

/** Spec §6.3 */
export const FIELD_TYPES = [
  'text',
  'long_text',
  'integer',
  'decimal',
  'money',
  'boolean',
  'date',
  'datetime',
  'enum',
  'multi_enum',
  'email',
  'phone',
  'url',
  'reference',
  'object',
] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

export const PII_CLASSIFICATIONS = ['none', 'personal', 'sensitive'] as const;
export type PiiClassification = (typeof PII_CLASSIFICATIONS)[number];

/**
 * Field labels that name special-category data (GDPR art. 9: health, religion, ethnicity,
 * politics, sex life) or national identifiers (ΑΜΚΑ, ΑΦΜ, ID card, passport). Matched on the
 * accent-folded label; medical stems only, so «θεραπεία» (a spa / hair treatment) stays ordinary.
 * A field with such a label is created `sensitive` unless the user explicitly confirms otherwise.
 */
const SENSITIVE_LABEL =
  /(allerg|αλλεργ|health|υγει|medical|ιατρ|διαγνω|diagnos|παθησ|ασθεν|φαρμακ|medicat|χρονι[αο] νοσ|νοσημ|disease|illness|εγκυμ|pregnan|αναπηρ|disab|θρησκ|religio|δογμα|εθνικοτ|εθνοτ|ethnic|πολιτικ[ηοε]σ? (πεποιθ|τοποθετ|κομμ)|politic|σεξουαλ|sexual|αμκα|amka|αφμ|(?<!\p{L})(afm|αδτ|ssn)(?!\p{L})|ταυτοτητ|διαβατηρ|passport|social security|national id|id card|ποινικ|criminal|βιομετρ|biometric|γενετικ|genetic)/u;

export function looksSensitiveLabel(...labels: (string | null | undefined)[]): boolean {
  const text = labels
    .filter(Boolean)
    .join(' ')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/ς/g, 'σ')
    .replace(/[_-]+/g, ' ');
  return SENSITIVE_LABEL.test(text);
}

export const DEFINITION_STATUSES = ['active', 'deprecated'] as const;
export type DefinitionStatus = (typeof DEFINITION_STATUSES)[number];

/** `contacts`, `object:<typeKey>`, `event:<eventType>` */
export const EntityTypeSchema = z
  .string()
  .regex(/^(contacts|object:[a-z][a-z0-9_]{0,47}|event:[a-z][a-z0-9_]{0,63})$/);

export const MachineKeySchema = z.string().regex(/^[a-z][a-z0-9_]{0,47}$/);
/** Event type keys may be a little longer (e.g. `appointment_completed_online`). */
export const EventKeySchema = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/);

export const FieldDefinitionSchema = z.object({
  id: UuidSchema,
  entityType: EntityTypeSchema,
  key: MachineKeySchema,
  label: z.string().min(1).max(120),
  labelI18n: z.record(z.string(), z.string()).default({}),
  type: z.enum(FIELD_TYPES),
  required: z.boolean(),
  allowedValues: z.array(z.string()).optional(),
  referenceObjectType: z.string().optional(),
  currency: z.string().length(3).optional(),
  piiClassification: z.enum(PII_CLASSIFICATIONS),
  searchable: z.boolean(),
  filterable: z.boolean(),
  sortable: z.boolean(),
  editable: z.boolean(),
  sourceMapping: z.record(z.string(), z.unknown()).optional(),
  status: z.enum(DEFINITION_STATUSES),
  schemaVersion: z.number().int(),
});
export type FieldDefinition = z.infer<typeof FieldDefinitionSchema>;

export const EVENT_SOURCES = ['manual', 'import', 'api', 'integration', 'system'] as const;
export type EventSource = (typeof EVENT_SOURCES)[number];

/** Built-in event types every tenant has (metrics rely on them). */
export const CORE_EVENT_TYPES = [
  'purchase_completed',
  'appointment_completed',
  'visit',
  'membership_started',
  'membership_expires',
] as const;

export const SCHEMA_PROPOSAL_STATUSES = [
  'draft',
  'pending_approval',
  'applied',
  'rejected',
  'failed',
  'superseded',
] as const;
export type SchemaProposalStatus = (typeof SCHEMA_PROPOSAL_STATUSES)[number];

export const RELATIONSHIP_CARDINALITIES = ['one_to_one', 'one_to_many', 'many_to_many'] as const;
export type RelationshipCardinality = (typeof RELATIONSHIP_CARDINALITIES)[number];

// ── Safe machine keys (shared by API and web; spec §6.3) ─────────────────────
/**
 * Safe machine keys from human labels / import headers ("Source import column names (including
 * Greek and spaces) map to stable safe keys"). Greek is transliterated (ELOT 743, simplified,
 * digraphs first), everything else folded to ASCII, non-alphanumerics become `_`.
 */
const DIGRAPHS: [RegExp, string][] = [
  [/ου/g, 'ou'],
  [/αυ(?=[θκξπσςτφχψ]|$)/g, 'af'],
  [/αυ/g, 'av'],
  [/ευ(?=[θκξπσςτφχψ]|$)/g, 'ef'],
  [/ευ/g, 'ev'],
  [/μπ/g, 'b'],
  [/ντ/g, 'nt'],
  [/γγ/g, 'ng'],
  [/γκ/g, 'gk'],
];

const LETTERS: Record<string, string> = {
  α: 'a',
  β: 'v',
  γ: 'g',
  δ: 'd',
  ε: 'e',
  ζ: 'z',
  η: 'i',
  θ: 'th',
  ι: 'i',
  κ: 'k',
  λ: 'l',
  μ: 'm',
  ν: 'n',
  ξ: 'x',
  ο: 'o',
  π: 'p',
  ρ: 'r',
  σ: 's',
  ς: 's',
  τ: 't',
  υ: 'y',
  φ: 'f',
  χ: 'ch',
  ψ: 'ps',
  ω: 'o',
};

/** Lowercase, accents removed (Greek tonos/dialytika included). */
function fold(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase();
}

export function transliterate(input: string): string {
  let s = fold(input);
  for (const [re, rep] of DIGRAPHS) s = s.replace(re, rep);
  return [...s].map((ch) => LETTERS[ch] ?? ch).join('');
}

/**
 * `"Ημ/νία ραντεβού"` → `im_nia_rantevou`, `"Order Date"` → `order_date`, `"1η επίσκεψη"` →
 * `f_1i_episkepsi`. Always matches `^[a-z][a-z0-9_]{0,maxLen-1}$`.
 */
export function toSafeKey(label: string, maxLen = 48): string {
  let k = transliterate(label)
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
  if (!k) k = 'field';
  if (!/^[a-z]/.test(k)) k = `f_${k}`;
  return k.slice(0, maxLen).replace(/_+$/, '') || 'field';
}

// ── Limits (bounded complexity, spec §6.4) ───────────────────────────────────

export const SCHEMA_LIMITS = {
  /** Active custom fields per entity (contacts, each object type, each event type). */
  fieldsPerEntity: 60,
  objectTypes: 10,
  eventTypes: 40,
  relationshipDefinitions: 20,
  allowedValues: 100,
  /** Type changes rewrite stored values; above this the change is refused (needs support). */
  typeChangeMaxValues: 50_000,
  /** Records per custom object type. */
  objectRecords: 20_000,
  ingestBatch: 500,
  apiKeys: 5,
} as const;

/** Types a field can be created with from the studio (reference/object need an object type). */
export const CREATABLE_FIELD_TYPES = FIELD_TYPES.filter((t) => t !== 'object');

// ── Field definitions ────────────────────────────────────────────────────────

const LabelI18nObject = z.object({
  el: z.string().trim().max(120).optional(),
  en: z.string().trim().max(120).optional(),
});
const LabelI18nSchema = LabelI18nObject.default({});
/** For PATCH bodies: absent means "unchanged" (no default, or every update would wipe it). */
const LabelI18nPatchSchema = LabelI18nObject.optional();

export const FieldViewSchema = FieldDefinitionSchema.extend({
  description: z.string().nullable(),
  sortOrder: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** How many records hold a value (contacts: attributes; objects: records). */
  valueCount: z.number().int().optional(),
});
export type FieldView = z.infer<typeof FieldViewSchema>;

const AllowedValuesSchema = z
  .array(z.string().trim().min(1).max(80))
  .max(SCHEMA_LIMITS.allowedValues)
  .refine((v) => new Set(v.map((x) => x.toLowerCase())).size === v.length, {
    message: 'allowed values must be unique',
  });

export const CreateFieldRequestSchema = z
  .object({
    entityType: EntityTypeSchema.default('contacts'),
    /** Optional: derived from the label (Greek transliterated) when omitted. Immutable after. */
    key: MachineKeySchema.optional(),
    label: z.string().trim().min(1).max(120),
    labelI18n: LabelI18nSchema,
    description: z.string().trim().max(500).optional(),
    type: z.enum(CREATABLE_FIELD_TYPES as unknown as [FieldType, ...FieldType[]]),
    required: z.boolean().default(false),
    allowedValues: AllowedValuesSchema.optional(),
    referenceObjectType: MachineKeySchema.optional(),
    currency: z.string().length(3).toUpperCase().optional(),
    piiClassification: z.enum(PII_CLASSIFICATIONS).default('none'),
    /**
     * A label that looks like special-category data (`looksSensitiveLabel`) makes the field
     * `sensitive` whatever classification was sent, unless the user explicitly confirms it is not.
     */
    confirmNotSensitive: z.boolean().optional(),
    searchable: z.boolean().default(false),
    filterable: z.boolean().default(true),
    sortable: z.boolean().default(false),
    editable: z.boolean().default(true),
    sourceMapping: z.record(z.string(), z.unknown()).optional(),
  })
  .refine((v) => !['enum', 'multi_enum'].includes(v.type) || (v.allowedValues?.length ?? 0) > 0, {
    message: 'enum fields need at least one allowed value',
    path: ['allowedValues'],
  })
  .refine((v) => v.type !== 'reference' || Boolean(v.referenceObjectType), {
    message: 'reference fields need an object type',
    path: ['referenceObjectType'],
  });
export type CreateFieldRequest = z.input<typeof CreateFieldRequestSchema>;

/** Key and type are not editable here: the key is immutable, the type uses the type-change flow (unknown keys → 422). */
export const UpdateFieldRequestSchema = z
  .strictObject({
    label: z.string().trim().min(1).max(120).optional(),
    labelI18n: LabelI18nPatchSchema,
    description: z.string().trim().max(500).nullable().optional(),
    required: z.boolean().optional(),
    /** Values can be added or renamed (`renameValues`); removing a value still in use is refused. */
    allowedValues: AllowedValuesSchema.optional(),
    renameValues: z.record(z.string(), z.string().trim().min(1).max(80)).optional(),
    currency: z.string().length(3).toUpperCase().optional(),
    piiClassification: z.enum(PII_CLASSIFICATIONS).optional(),
    /** Required to keep a sensitive-looking label below `sensitive` (see CreateFieldRequestSchema). */
    confirmNotSensitive: z.boolean().optional(),
    searchable: z.boolean().optional(),
    filterable: z.boolean().optional(),
    sortable: z.boolean().optional(),
    editable: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'nothing to update' });
export type UpdateFieldRequest = z.input<typeof UpdateFieldRequestSchema>;

/** Field types a type change can target (reference/object hold links, not converted values). */
export const CONVERTIBLE_FIELD_TYPES = FIELD_TYPES.filter(
  (t) => t !== 'reference' && t !== 'object',
);

export const TypeChangeRequestSchema = z.object({
  toType: z.enum(CONVERTIBLE_FIELD_TYPES as unknown as [FieldType, ...FieldType[]]),
  allowedValues: AllowedValuesSchema.optional(),
  currency: z.string().length(3).toUpperCase().optional(),
});
export type TypeChangeRequest = z.input<typeof TypeChangeRequestSchema>;

/** `lossless`: every value converts and converts back; `lossy`: converts, formatting may change; `unsafe`: some values cannot convert; `unsupported`: no conversion between these types. */
export const TYPE_CHANGE_SAFETY = ['lossless', 'lossy', 'unsafe', 'unsupported'] as const;
export type TypeChangeSafety = (typeof TYPE_CHANGE_SAFETY)[number];

export const SchemaDependentSchema = z.object({
  kind: z.string(),
  id: z.string(),
  label: z.string(),
});
export type SchemaDependent = z.infer<typeof SchemaDependentSchema>;

export const TypeChangePreviewSchema = z.object({
  fieldId: UuidSchema,
  fromType: z.enum(FIELD_TYPES),
  toType: z.enum(FIELD_TYPES),
  safety: z.enum(TYPE_CHANGE_SAFETY),
  total: z.number().int(),
  convertible: z.number().int(),
  changed: z.number().int(),
  failing: z.number().int(),
  samples: z.array(
    z.object({
      recordRef: z.string(),
      before: z.unknown(),
      after: z.unknown().nullable(),
      ok: z.boolean(),
      reason: z.string().optional(),
    }),
  ),
  dependents: z.array(SchemaDependentSchema),
  /** Echo on apply: binds the apply to this preview (schema version + data fingerprint). */
  previewToken: z.string(),
  schemaVersion: z.number().int(),
});
export type TypeChangePreview = z.infer<typeof TypeChangePreviewSchema>;

export const TypeChangeApplyRequestSchema = TypeChangeRequestSchema.extend({
  previewToken: z.string().min(10).max(200),
});
export type TypeChangeApplyRequest = z.input<typeof TypeChangeApplyRequestSchema>;

// ── Events ───────────────────────────────────────────────────────────────────

/**
 * Which amount a sales DOCUMENT event stores as its value: the lines' gross (with VAT, the
 * default) or net (without VAT). Chosen once per tenant: saved on the document event type, so
 * every later import uses the same rule (ADR-SCH-13).
 */
export const SALES_VALUE_BASES = ['gross', 'net'] as const;
export type SalesValueBasis = (typeof SALES_VALUE_BASES)[number];

/**
 * Sales-lines model (ADR-SCH-13): a `document` event (one receipt / invoice, with the value) and
 * its `line` events (one per product line, without a value, so spend is never counted twice),
 * joined by `groupProperty` (the document number). Property keys name the line event's fields
 * that dashboards and the assistant read (item revenue, units, category, product).
 */
export const EventSalesModelSchema = z.object({
  role: z.enum(['line', 'document']),
  /** The other side: the document event of a line event, or the line event of a document. */
  partner: EventKeySchema,
  groupProperty: MachineKeySchema,
  amountProperty: MachineKeySchema.nullable().default(null),
  quantityProperty: MachineKeySchema.nullable().default(null),
  categoryProperty: MachineKeySchema.nullable().default(null),
  productNameProperty: MachineKeySchema.nullable().default(null),
  /** `reference` property pointing at the product catalogue record. */
  productProperty: MachineKeySchema.nullable().default(null),
});
export type EventSalesModel = z.infer<typeof EventSalesModelSchema>;

export const EventDefinitionViewSchema = z.object({
  id: UuidSchema,
  key: EventKeySchema,
  label: z.string(),
  labelI18n: z.record(z.string(), z.string()),
  description: z.string().nullable(),
  hasValue: z.boolean(),
  countsAsVisit: z.boolean(),
  /** Counts as a purchase in purchase metrics (purchase_count, last purchase, segments). */
  countsAsPurchase: z.boolean(),
  /** Sales documents: the value is the lines' gross or net amount (null: not a document type). */
  valueBasis: z.enum(SALES_VALUE_BASES).nullable().default(null),
  salesModel: EventSalesModelSchema.nullable().default(null),
  isCore: z.boolean(),
  status: z.enum(DEFINITION_STATUSES),
  schemaVersion: z.number().int(),
  eventCount: z.number().int(),
  lastOccurredAt: z.string().nullable(),
  fields: z.array(FieldViewSchema),
  createdAt: z.string(),
});
export type EventDefinitionView = z.infer<typeof EventDefinitionViewSchema>;

export const CreateEventDefinitionRequestSchema = z.object({
  key: EventKeySchema.optional(),
  label: z.string().trim().min(1).max(120),
  labelI18n: LabelI18nSchema,
  description: z.string().trim().max(500).optional(),
  hasValue: z.boolean().default(false),
  countsAsVisit: z.boolean().default(false),
  /** Omitted: true only for the core `purchase_completed` key. */
  countsAsPurchase: z.boolean().optional(),
  valueBasis: z.enum(SALES_VALUE_BASES).nullable().optional(),
});
export type CreateEventDefinitionRequest = z.input<typeof CreateEventDefinitionRequestSchema>;

export const UpdateEventDefinitionRequestSchema = z
  .strictObject({
    label: z.string().trim().min(1).max(120).optional(),
    labelI18n: LabelI18nPatchSchema,
    description: z.string().trim().max(500).nullable().optional(),
    hasValue: z.boolean().optional(),
    countsAsVisit: z.boolean().optional(),
    countsAsPurchase: z.boolean().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'nothing to update' });
export type UpdateEventDefinitionRequest = z.input<typeof UpdateEventDefinitionRequestSchema>;

export const CustomerEventViewSchema = z.object({
  id: UuidSchema,
  contactId: UuidSchema.nullable(),
  contactName: z.string().nullable(),
  eventType: z.string(),
  eventLabel: z.string(),
  occurredAt: z.string(),
  ingestedAt: z.string(),
  properties: z.record(z.string(), z.unknown()),
  valueCents: z.number().int().nullable(),
  currency: z.string().nullable(),
  source: z.enum(EVENT_SOURCES),
  sourceId: z.string().nullable(),
  importId: UuidSchema.nullable(),
  /** Anonymous sale (no contact on purpose; not in the unmatched inbox). */
  anonymous: z.boolean().default(false),
});
export type CustomerEventView = z.infer<typeof CustomerEventViewSchema>;

const PropertyValueSchema = z.union([
  z.string().max(5000),
  z.number(),
  z.boolean(),
  z.array(z.string().max(200)).max(100),
  z.null(),
]);

export const CreateCustomerEventRequestSchema = z.object({
  contactId: UuidSchema,
  eventType: EventKeySchema,
  occurredAt: IsoDateTimeSchema,
  valueCents: z.number().int().min(-100_000_000).max(100_000_000).optional(),
  currency: z.string().length(3).toUpperCase().optional(),
  /** Keyed by event field key (`event:<type>` field definitions). */
  properties: z.record(z.string(), PropertyValueSchema).default({}),
  /** Client-supplied id for idempotent retries (manual source). */
  sourceId: z.string().trim().min(1).max(200).optional(),
});
export type CreateCustomerEventRequest = z.input<typeof CreateCustomerEventRequestSchema>;

export const CustomerEventListQuerySchema = z.object({
  contactId: UuidSchema.optional(),
  eventType: EventKeySchema.optional(),
  unmatched: z
    .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
    .transform((v) => v === true || v === 'true' || v === '1')
    .optional(),
  importId: UuidSchema.optional(),
  /**
   * Event browser: property filters as JSON (`[{"property","op","value"}]`, same grammar as
   * segment `where`). Needs `eventType`.
   */
  where: z
    .string()
    .max(4000)
    .transform((v, ctx) => {
      try {
        return z
          .array(EventPropertyFilterSchema)
          .max(QUERY_LIMITS.maxPropertyFilters)
          .parse(JSON.parse(v));
      } catch {
        ctx.addIssue({ code: 'custom', message: 'Invalid property filters' });
        return z.NEVER;
      }
    })
    .optional(),
  /** Inclusive local dates (tenant timezone). */
  from: IsoDateSchema.optional(),
  to: IsoDateSchema.optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type CustomerEventListQuery = z.input<typeof CustomerEventListQuerySchema>;

export const CustomerEventListResponseSchema = z.object({
  items: z.array(CustomerEventViewSchema),
  nextCursor: z.string().nullable(),
});

export const AssignCustomerEventRequestSchema = z.object({ contactId: UuidSchema });

/** Server-to-server ingestion (tenant API key). Idempotent by (source `api`, sourceId). */
export const IngestEventSchema = z.object({
  sourceId: z.string().trim().min(1).max(200),
  eventType: EventKeySchema,
  occurredAt: IsoDateTimeSchema,
  contact: z
    .object({
      email: z.string().trim().max(254).optional(),
      phone: z.string().trim().max(32).optional(),
      id: UuidSchema.optional(),
    })
    .refine((c) => Boolean(c.email || c.phone || c.id), { message: 'contact email, phone or id' }),
  valueCents: z.number().int().min(-100_000_000).max(100_000_000).optional(),
  currency: z.string().length(3).toUpperCase().optional(),
  properties: z.record(z.string(), PropertyValueSchema).default({}),
});
export type IngestEvent = z.input<typeof IngestEventSchema>;

export const IngestEventsRequestSchema = z.object({
  events: z.array(z.unknown()).min(1).max(SCHEMA_LIMITS.ingestBatch),
});

export const INGEST_RESULT_STATUSES = ['created', 'duplicate', 'unmatched', 'rejected'] as const;
export const IngestEventsResponseSchema = z.object({
  results: z.array(
    z.object({
      index: z.number().int(),
      sourceId: z.string().nullable(),
      status: z.enum(INGEST_RESULT_STATUSES),
      eventId: UuidSchema.nullable(),
      error: z.string().optional(),
    }),
  ),
  counts: z.record(z.enum(INGEST_RESULT_STATUSES), z.number().int()),
});
export type IngestEventsResponse = z.infer<typeof IngestEventsResponseSchema>;

export const ApiKeyViewSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  prefix: z.string(),
  scopes: z.array(z.string()),
  lastUsedAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type ApiKeyView = z.infer<typeof ApiKeyViewSchema>;

export const CreateApiKeyRequestSchema = z.object({ name: z.string().trim().min(1).max(80) });
export const CreateApiKeyResponseSchema = z.object({
  key: ApiKeyViewSchema,
  /** Shown once. */
  secret: z.string(),
});

// ── Objects & relationships ──────────────────────────────────────────────────

export const ObjectDefinitionViewSchema = z.object({
  id: UuidSchema,
  key: MachineKeySchema,
  label: z.string(),
  labelPlural: z.string(),
  labelI18n: z.record(z.string(), z.string()),
  displayFieldKey: z.string().nullable(),
  status: z.enum(DEFINITION_STATUSES),
  schemaVersion: z.number().int(),
  recordCount: z.number().int(),
  fields: z.array(FieldViewSchema),
  createdAt: z.string(),
});
export type ObjectDefinitionView = z.infer<typeof ObjectDefinitionViewSchema>;

export const CreateObjectDefinitionRequestSchema = z.object({
  key: MachineKeySchema.optional(),
  label: z.string().trim().min(1).max(80),
  labelPlural: z.string().trim().min(1).max(80),
  labelI18n: LabelI18nSchema,
  /** A text field with this key is created as the record title. */
  displayFieldLabel: z.string().trim().min(1).max(120).default('Όνομα'),
});
export type CreateObjectDefinitionRequest = z.input<typeof CreateObjectDefinitionRequestSchema>;

export const UpdateObjectDefinitionRequestSchema = z
  .strictObject({
    label: z.string().trim().min(1).max(80).optional(),
    labelPlural: z.string().trim().min(1).max(80).optional(),
    labelI18n: LabelI18nPatchSchema,
    displayFieldKey: MachineKeySchema.optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'nothing to update' });

export const ObjectRecordSchema = z.object({
  id: UuidSchema,
  objectKey: z.string(),
  externalId: z.string().nullable(),
  displayName: z.string().nullable(),
  /** Keyed by field key (object fields). */
  attributes: z.record(z.string(), z.unknown()),
  status: z.enum(['active', 'archived']),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ObjectRecord = z.infer<typeof ObjectRecordSchema>;

export const ObjectRecordListQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  status: z.enum(['active', 'archived']).default('active'),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const ObjectRecordListResponseSchema = z.object({
  items: z.array(ObjectRecordSchema),
  nextCursor: z.string().nullable(),
  total: z.number().int(),
});

export const UpsertObjectRecordRequestSchema = z.object({
  externalId: z.string().trim().max(120).nullable().optional(),
  attributes: z.record(z.string(), PropertyValueSchema).default({}),
});
export type UpsertObjectRecordRequest = z.input<typeof UpsertObjectRecordRequestSchema>;

export const RelationshipDefinitionViewSchema = z.object({
  id: UuidSchema,
  key: MachineKeySchema,
  label: z.string(),
  fromEntity: z.string(),
  toEntity: z.string(),
  cardinality: z.enum(RELATIONSHIP_CARDINALITIES),
  status: z.enum(DEFINITION_STATUSES),
  linkCount: z.number().int(),
});
export type RelationshipDefinitionView = z.infer<typeof RelationshipDefinitionViewSchema>;

const RelEntitySchema = z.string().regex(/^(contacts|object:[a-z][a-z0-9_]{0,47})$/);
export const CreateRelationshipDefinitionRequestSchema = z.object({
  key: MachineKeySchema.optional(),
  label: z.string().trim().min(1).max(80),
  fromEntity: RelEntitySchema,
  toEntity: RelEntitySchema,
  cardinality: z.enum(RELATIONSHIP_CARDINALITIES).default('many_to_many'),
});
export type CreateRelationshipDefinitionRequest = z.input<
  typeof CreateRelationshipDefinitionRequestSchema
>;

export const RelationshipLinkSchema = z.object({
  id: UuidSchema,
  definitionId: UuidSchema,
  definitionKey: z.string(),
  definitionLabel: z.string(),
  direction: z.enum(['outgoing', 'incoming']),
  otherType: z.string(),
  otherId: UuidSchema,
  otherLabel: z.string().nullable(),
  attributes: z.record(z.string(), z.unknown()),
  occurredAt: z.string().nullable(),
});
export type RelationshipLink = z.infer<typeof RelationshipLinkSchema>;

export const CreateRelationshipLinkRequestSchema = z.object({
  fromId: UuidSchema,
  toId: UuidSchema,
  attributes: z.record(z.string(), PropertyValueSchema).default({}),
  occurredAt: IsoDateTimeSchema.optional(),
});

/** «Πελάτες που το αγόρασαν»: contacts whose line events reference this record. */
export const RecordBuyersResponseSchema = z.object({
  items: z.array(
    z.object({
      contactId: UuidSchema,
      name: z.string(),
      lines: z.number().int(),
      quantity: z.number().nullable(),
      amountCents: z.number().int().nullable(),
      lastAt: z.string(),
    }),
  ),
  /** Lines of anonymous sales (no contact) for this record. */
  anonymousLines: z.number().int(),
  total: z.number().int(),
  eventKey: z.string().nullable(),
});
export type RecordBuyersResponse = z.infer<typeof RecordBuyersResponseSchema>;

export const ObjectRecordDetailSchema = z.object({
  record: ObjectRecordSchema,
  definition: ObjectDefinitionViewSchema,
  links: z.array(RelationshipLinkSchema),
});
export type ObjectRecordDetail = z.infer<typeof ObjectRecordDetailSchema>;

// ── Versions / overview ──────────────────────────────────────────────────────

export const SCHEMA_CHANGE_KINDS = [
  'field_added',
  'field_updated',
  'field_type_changed',
  'field_deprecated',
  'field_restored',
  'event_added',
  'event_updated',
  'event_deprecated',
  'event_restored',
  'object_added',
  'object_updated',
  'object_deprecated',
  'relationship_added',
  'relationship_deprecated',
  'proposal_applied',
  'reverted',
] as const;
export type SchemaChangeKind = (typeof SCHEMA_CHANGE_KINDS)[number];

export const SchemaVersionViewSchema = z.object({
  version: z.number().int(),
  kind: z.enum(SCHEMA_CHANGE_KINDS),
  summary: z.record(z.string(), z.unknown()),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  revertible: z.boolean(),
  revertedBy: z.number().int().nullable(),
});
export type SchemaVersionView = z.infer<typeof SchemaVersionViewSchema>;

export const SchemaOverviewSchema = z.object({
  schemaVersion: z.number().int(),
  fields: z.array(FieldViewSchema),
  events: z.array(EventDefinitionViewSchema),
  objects: z.array(ObjectDefinitionViewSchema),
  relationships: z.array(RelationshipDefinitionViewSchema),
  limits: z.record(z.string(), z.number()),
});
export type SchemaOverview = z.infer<typeof SchemaOverviewSchema>;

export const SchemaHistoryResponseSchema = z.object({ items: z.array(SchemaVersionViewSchema) });

// ── Advanced import (AI mapping, spec §6.5) ──────────────────────────────────

export const DATE_FORMATS = [
  'DD/MM/YYYY',
  'MM/DD/YYYY',
  'YYYY-MM-DD',
  'DD.MM.YYYY',
  'DD-MM-YYYY',
] as const;
export type DateFormat = (typeof DATE_FORMATS)[number];

export const IMPORT_COLUMN_KINDS = [
  /** core contact column (`target` = full_name, phone, email, …) */
  'contact',
  /** existing custom contact field (`fieldId`) */
  'contact_field',
  /** create a new custom contact field (`newField`) */
  'new_contact_field',
  /** when the event happened (date or datetime) */
  'event_date',
  /** time of day, combined with `event_date` */
  'event_time',
  /** monetary value of the event */
  'event_value',
  /** event property (`propertyKey`, created as an event field when new) */
  'event_property',
  /** stable id from the source system (order number): makes re-imports idempotent */
  'event_source_id',
  'ignore',
] as const;
export type ImportColumnKind = (typeof IMPORT_COLUMN_KINDS)[number];

const CORE_TARGETS = [
  'first_name',
  'last_name',
  'full_name',
  'email',
  'phone',
  'tags',
  'birthday',
] as const;

export const NewFieldSpecSchema = z.object({
  key: MachineKeySchema,
  label: z.string().trim().min(1).max(120),
  type: z.enum([
    'text',
    'long_text',
    'integer',
    'decimal',
    'money',
    'boolean',
    'date',
    'datetime',
    'enum',
    'multi_enum',
    'url',
  ]),
  allowedValues: z
    .array(z.string().trim().min(1).max(80))
    .max(SCHEMA_LIMITS.allowedValues)
    .optional(),
  piiClassification: z.enum(PII_CLASSIFICATIONS).default('none'),
  /** The label fell back to the file's column header (text from the file): the UI asks to check it. */
  labelFromHeader: z.boolean().optional(),
});
export type NewFieldSpec = z.infer<typeof NewFieldSpecSchema>;

export const ImportColumnMappingSchema = z.object({
  index: z.number().int().min(0).max(99),
  header: z.string().max(200),
  kind: z.enum(IMPORT_COLUMN_KINDS),
  target: z.enum(CORE_TARGETS).optional(),
  fieldId: UuidSchema.optional(),
  newField: NewFieldSpecSchema.optional(),
  /** Event this column belongs to (event_* kinds). */
  eventKey: EventKeySchema.optional(),
  propertyKey: MachineKeySchema.optional(),
  propertyType: z
    .enum(['text', 'long_text', 'integer', 'decimal', 'money', 'boolean', 'date', 'enum'])
    .optional(),
  confidence: z.number().min(0).max(1),
  reason: z.string().max(300).optional(),
  /**
   * Set by the server when it had to turn the suggestion into `ignore` (validation code such as
   * `unknown_event`, `invalid_type`): the UI says why instead of showing the model's own reason.
   */
  rejection: z.string().max(40).optional(),
});
export type ImportColumnMapping = z.infer<typeof ImportColumnMappingSchema>;

export const ImportEventMappingSchema = z.object({
  key: EventKeySchema,
  label: z.string().trim().min(1).max(120),
  isNew: z.boolean(),
  countsAsVisit: z.boolean().default(false),
  /** Counts as a purchase (new event types only; an existing type keeps its own setting). */
  countsAsPurchase: z.boolean().default(false),
  hasValue: z.boolean().default(false),
  /**
   * `event`: one event per row (default). Sales-lines files: `line` = one event per row (a product
   * line, never with a value) and `document` = one event per document number, built from its
   * lines after the commit (value = the lines' total, see `document`). A document event has no
   * columns of its own.
   */
  role: z.enum(['event', 'line', 'document']).default('event'),
});
export type ImportEventMapping = z.infer<typeof ImportEventMappingSchema>;

export const ImportColumnStatsSchema = z.object({
  index: z.number().int(),
  header: z.string(),
  filled: z.number(),
  distinct: z.number().int(),
  kinds: z.record(z.string(), z.number()),
  dateFormats: z.array(z.enum(DATE_FORMATS)),
  /** Only for short, low-cardinality, non-personal columns (enum candidates). */
  topValues: z.array(z.string()).optional(),
});
export type ImportColumnStats = z.infer<typeof ImportColumnStatsSchema>;

const ImportColumnRefSchema = z.object({
  index: z.number().int().min(0).max(99),
  header: z.string().max(200),
});

/**
 * «Ομαδοποίηση γραμμών ανά αριθμό παραστατικού» (sales-lines files, ADR-SCH-13): the rows are
 * the lines (`lineEventKey`) of documents (`eventKey`). Property keys are the LINE event's.
 */
export const ImportDocumentGroupingSchema = z.object({
  eventKey: EventKeySchema,
  lineEventKey: EventKeySchema,
  /** Line property with the document number (also stored on the document event). */
  groupProperty: MachineKeySchema,
  groupHeader: z.string().max(200),
  /** «Αξία συναλλαγής: με ΦΠΑ / χωρίς ΦΠΑ». */
  valueBasis: z.enum(SALES_VALUE_BASES).default('gross'),
  /** The document event type already has a basis (an earlier import): it cannot change here. */
  valueBasisLocked: z.boolean().default(false),
  grossProperty: MachineKeySchema.nullable().default(null),
  netProperty: MachineKeySchema.nullable().default(null),
  vatProperty: MachineKeySchema.nullable().default(null),
  quantityProperty: MachineKeySchema.nullable().default(null),
  categoryProperty: MachineKeySchema.nullable().default(null),
  productNameProperty: MachineKeySchema.nullable().default(null),
  /** Line properties with one value per document (type, payment method…): copied onto it. */
  copyProperties: z.array(MachineKeySchema).max(10).default([]),
  /** Credit notes: lines whose `creditNoteProperty` is one of these values count negative. */
  creditNoteProperty: MachineKeySchema.nullable().default(null),
  creditNoteValues: z.array(z.string().trim().min(1).max(80)).max(5).default([]),
  /**
   * Event type the credit-note documents are recorded as («Επιστροφή αγοράς», negative value,
   * neither a purchase nor a visit). Null when there are no credit notes.
   */
  creditEventKey: EventKeySchema.nullable().default(null),
});
export type ImportDocumentGrouping = z.infer<typeof ImportDocumentGroupingSchema>;

/**
 * «Δημιουργία καταλόγου προϊόντων» (opt-in, on by default for sales files): products upserted by
 * code into the custom object `objectKey` (records keyed by `external_id`), and each line gets a
 * `reference` property to its product.
 */
export const ImportProductCatalogSchema = z.object({
  enabled: z.boolean(),
  objectKey: MachineKeySchema.default('product'),
  /** The object type does not exist yet (created on approval). */
  isNew: z.boolean().default(true),
  code: ImportColumnRefSchema,
  name: ImportColumnRefSchema.nullable().default(null),
  description: ImportColumnRefSchema.nullable().default(null),
  category: ImportColumnRefSchema.nullable().default(null),
  unit: ImportColumnRefSchema.nullable().default(null),
  price: ImportColumnRefSchema.nullable().default(null),
});
export type ImportProductCatalog = z.infer<typeof ImportProductCatalogSchema>;

/** Proposal shape (AI output after server validation, and the user-edited version). */
export const ImportMappingProposalSchema = z.object({
  rowGrain: z.enum(['contact', 'event']),
  dateFormat: z.enum(DATE_FORMATS).nullable(),
  columns: z.array(ImportColumnMappingSchema).max(100),
  events: z.array(ImportEventMappingSchema).max(5),
  warnings: z.array(z.string().max(300)).max(20).default([]),
  assumptions: z.array(z.string().max(300)).max(20).default([]),
  /**
   * Anonymous retail sales: rows without email/phone whose cell in column `index` is one of
   * `values` (e.g. «C0000» / «Πελάτης Λιανικής») are recorded as anonymous sales: no contact is
   * created, they never enter the unmatched inbox, and they still count in revenue.
   */
  anonymousCustomer: z
    .object({
      index: z.number().int().min(0).max(99),
      header: z.string().max(200),
      values: z.array(z.string().trim().min(1).max(80)).min(1).max(5),
      rows: z.number().int().min(0).optional(),
    })
    .nullable()
    .default(null),
  document: ImportDocumentGroupingSchema.nullable().default(null),
  products: ImportProductCatalogSchema.nullable().default(null),
  /**
   * Set when the user asked the assistant but the proposal came from the built-in rules:
   * `unavailable` (provider outage), `incomplete` (no usable answer, e.g. cut off on a very wide
   * file). The review screen shows a banner with a retry.
   */
  aiFallback: z.enum(['unavailable', 'incomplete']).nullable().default(null),
});
export type ImportMappingProposal = z.infer<typeof ImportMappingProposalSchema>;

export const IMPORT_MAPPING_STATUSES = ['none', 'proposed', 'approved', 'rejected'] as const;

export const ImportEventStatsSchema = z.object({
  created: z.number().int().default(0),
  duplicates: z.number().int().default(0),
  unmatched: z.number().int().default(0),
  rejected: z.number().int().default(0),
  /** Anonymous retail sales recorded without a contact (not "unmatched", not errors). */
  anonymous: z.number().int().default(0),
  /** File rows behind them (the contacts step calls these invalid: no email/phone). */
  anonymousRows: z.number().int().default(0),
  /** Sales documents built from the lines (created or refreshed). */
  documents: z.number().int().default(0),
  /** Product catalogue records created or updated. */
  products: z.number().int().default(0),
  problems: z
    .array(
      z.object({ rowNumber: z.number().int(), code: z.string(), column: z.string().optional() }),
    )
    .default([]),
});
export type ImportEventStats = z.infer<typeof ImportEventStatsSchema>;

export const AdvancedImportStateSchema = z.object({
  importId: UuidSchema,
  status: z.enum(IMPORT_MAPPING_STATUSES),
  source: z.enum(['ai', 'heuristic']).nullable(),
  proposal: ImportMappingProposalSchema.nullable(),
  approved: ImportMappingProposalSchema.nullable(),
  stats: z.array(ImportColumnStatsSchema),
  creditsUsed: z.number().int(),
  eventStats: ImportEventStatsSchema,
  aiAvailable: z.boolean(),
});
export type AdvancedImportState = z.infer<typeof AdvancedImportStateSchema>;

export const ApproveImportMappingRequestSchema = ImportMappingProposalSchema;
export type ApproveImportMappingRequest = z.input<typeof ApproveImportMappingRequestSchema>;

export const ApproveImportMappingResponseSchema = z.object({
  state: AdvancedImportStateSchema,
  /** Column targets for the contacts mapping step (`field:<id>` for custom fields). */
  contactColumns: z.array(z.object({ index: z.number().int(), target: z.string() })),
  createdFields: z.array(FieldViewSchema),
  schemaVersion: z.number().int(),
});
export type ApproveImportMappingResponse = z.infer<typeof ApproveImportMappingResponseSchema>;

export const ImportEventsPreviewSchema = z.object({
  events: z.number().int(),
  byType: z.record(z.string(), z.number().int()),
  matchedToContacts: z.number().int(),
  unmatched: z.number().int(),
  rejected: z.number().int(),
  problems: ImportEventStatsSchema.shape.problems,
  /** Rows the contacts dry run calls duplicates that are really further events of one customer. */
  repeatRows: z.number().int(),
  /** Anonymous retail sales (events) and the rows they come from. */
  anonymous: z.number().int().default(0),
  /** Rows the contacts dry run calls invalid (no email/phone) that are really anonymous sales. */
  anonymousRows: z.number().int().default(0),
  /** Sales documents the lines group into, and distinct product codes (catalogue). */
  documents: z.number().int().default(0),
  products: z.number().int().default(0),
});
export type ImportEventsPreview = z.infer<typeof ImportEventsPreviewSchema>;

// ── Metrics ──────────────────────────────────────────────────────────────────

export const ROLLING_WINDOWS = [30, 90, 180, 365] as const;

export const ContactMetricsViewSchema = z.object({
  contactId: UuidSchema,
  lifetime: z.object({
    purchaseCount: z.number().int(),
    totalSpendCents: z.number().int(),
    lastPurchaseAt: z.string().nullable(),
    visitCount: z.number().int(),
    lastVisitAt: z.string().nullable(),
    firstSeenAt: z.string().nullable(),
    lastEventAt: z.string().nullable(),
  }),
  eventCounts: z.record(z.string(), z.number().int()),
  spendByMonth: z.record(z.string(), z.number().int()),
  rolling: z.array(
    z.object({
      days: z.number().int(),
      purchaseCount: z.number().int(),
      spendCents: z.number().int(),
      visitCount: z.number().int(),
      eventCount: z.number().int(),
    }),
  ),
});
export type ContactMetricsView = z.infer<typeof ContactMetricsViewSchema>;
